import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../integrations/supabase/client';

interface UserProfile {
  role: 'rh' | 'diretor' | 'gerente' | 'corretor';
  first_name: string;
  last_name: string;
  email: string;
  force_password_change: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = async (authUser: User) => {
    try {
      console.log('👤 AuthContext: Carregando perfil para userId:', authUser.id);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('role, first_name, last_name, email, force_password_change')
        .eq('user_id', authUser.id)
        .maybeSingle();
      
      if (error) {
        console.error('❌ AuthContext: Erro ao carregar perfil:', error);
      }
      
      if (!data) {
        console.warn('⚠️ AuthContext: Nenhum perfil encontrado. Criando perfil padrão...');
        const firstName = (authUser.user_metadata?.first_name as string) || authUser.email?.split('@')[0] || 'Usuário';
        const lastName = (authUser.user_metadata?.last_name as string) || '';
        const email = authUser.email || '';
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            user_id: authUser.id,
            role: 'corretor',
            first_name: firstName,
            last_name: lastName,
            email,
            force_password_change: false
          });
        if (insertError) {
          console.error('❌ AuthContext: Falha ao criar perfil padrão:', insertError);
        } else {
          console.log('✅ AuthContext: Perfil padrão criado com sucesso');
        }
        // Tentar carregar novamente
        const { data: created } = await supabase
          .from('profiles')
          .select('role, first_name, last_name, email, force_password_change')
          .eq('user_id', authUser.id)
          .single();
        if (created) {
          setProfile({
            role: created.role,
            first_name: created.first_name,
            last_name: created.last_name,
            email: created.email,
            force_password_change: created.force_password_change || false,
          });
        }
        return;
      }
      
      console.log('✅ AuthContext: Perfil carregado com sucesso:', data);
      const profile: UserProfile = {
        role: data.role,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        force_password_change: data.force_password_change || false,
      };
      setProfile(profile);
    } catch (err) {
      console.error('💥 AuthContext: Erro inesperado ao carregar/criar perfil:', err);
    }
  };

  useEffect(() => {
    console.log('🔐 AuthContext: Inicializando autenticação...');
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        console.log('🔐 AuthContext: Evento de auth:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          console.log('🔐 AuthContext: Usuário logado, carregando perfil...');
          // Load profile data when user signs in
          setTimeout(() => {
            loadProfile(session.user!);
          }, 0);
        } else {
          console.log('🔐 AuthContext: Nenhum usuário logado');
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }: { data: { session: Session | null } }) => {
      console.log('🔐 AuthContext: Sessão existente:', session?.user?.email);
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        console.log('🔐 AuthContext: Carregando perfil para sessão existente...');
        loadProfile(session.user!);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, profile, loading }}>
      {children}
    </AuthContext.Provider>
  );
};